import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Grid,
	InputAdornment,
	Popover,
	TextField,
	Typography,
} from "@mui/material"
import { addDays, format } from "date-fns"
import { useEffect, useState } from "react"
import { DateRangePicker, defaultStaticRanges } from "react-date-range"
import * as locales from "react-date-range/dist/locale"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css"

import { useTranslation } from "react-i18next"
import { IDatePicker } from "../../views/Distinte/List"
import BankAutoComplete from "./bank-autocomplete"
import CampaignAutoComplete from "./campaign-autocomplete"
import CorporateAutoComplete from "./corporate-autocomplete"
import StatusMultiSelect from "./status-multi-select"

interface IFiltersFileProps {
	filterBank: string | undefined
	setFilterBank: (bank: string | undefined) => void
	filterCorporate: string | undefined
	setFilterCorporate: (corporate: string | undefined) => void
	filterCampaign: string | undefined
	setFilterCampaign: (campaign: string | undefined) => void
	datePicker: IDatePicker[]
	setDatePicker: (value: IDatePicker[]) => void
	handleSearch: () => void
	statuses: string[]
	setStatuses: (statusList: string[]) => void
}

function translateRange(dictionary) {
	return (item) =>
		dictionary[item.label] ? { ...item, label: dictionary[item.label] } : item
}

const FiltersFile = ({
	filterBank,
	setFilterBank,
	filterCampaign,
	setFilterCampaign,
	filterCorporate,
	setFilterCorporate,
	datePicker,
	handleSearch,
	setDatePicker,
	statuses,
	setStatuses,
}: IFiltersFileProps) => {
	const { t } = useTranslation()
	const [anchorEl, setAnchorEl] = useState(null)
	const [filtersCount, setFiltersCount] = useState<number>(0)
	const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)

	const staticRangesLabels = {
		Today: "Oggi",
		Yesterday: "Ieri",
		"This Week": "Questa settimana",
		"Last Week": "Settimana scorsa",
		"This Month": "Questo mese",
		"Last Month": "Mese scorso",
	}

	const inputRangesLabels = {
		"days up to today": "giorni prima di oggi",
		"days starting today": "giorni da oggi",
	}

	const ruStaticRanges = defaultStaticRanges.map(
		translateRange(staticRangesLabels)
	)
	// const ruInputRanges = defaultInputRanges.map(translateRange(inputRangesLabels));

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const open = Boolean(anchorEl)

	const handleClickToggleDatePicker = () => {
		/*if (openDatePicker) {
			handleSearch()
		}*/

		setOpenDatePicker(!openDatePicker)
	}

	// eslint-disable-next-line
	const resetFilter = () => {
		setDatePicker([
			{
				startDate: undefined,
				endDate: undefined,
				key: "selection",
			},
		])
		setFilterCampaign(undefined)
		setFilterCorporate(undefined)
		setStatuses([])
		setFiltersCount(0)
	}

	useEffect(() => {
		let count: number = 0
		if (statuses.length > 0) count++
		if (filterBank) count++
		if (filterCorporate) count++
		if (filterCampaign) count++
		if (!!datePicker?.[0]?.startDate || !!datePicker?.[0]?.endDate) count++
		setFiltersCount(count)
	}, [filterBank, filterCorporate, filterCampaign, datePicker, statuses])

	const localesSelected =
		window.localStorage.getItem("i18nextLng") === "en"
			? "enGB"
			: (window.localStorage.getItem("i18nextLng") ?? "it")

	return (
		<div>
			<Button variant="outlined" onClick={handleClick}>
				<FilterAltOutlinedIcon />
				<Typography
					component="span"
					variant="subtitle2"
					style={{ textTransform: "capitalize" }}
				>
					{t("filter.label", { ns: "distinte" })}
				</Typography>
				<Typography
					component="span"
					variant="caption"
					style={{ textTransform: "lowercase" }}
				>
					({filtersCount})
				</Typography>
			</Button>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Grid
					container
					gap={"16px"}
					sx={{ padding: "32px", width: "550px", maxWidth: "90vw" }}
				>
					<Grid
						item
						xs={12}
						sm={12}
						md={12}
						style={{ maxWidth: "230px", width: "100%" }}
					>
						<BankAutoComplete
							disableClearable={true}
							filterBank={filterBank}
							setFilterBank={setFilterBank}
						/>
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}
						md={6}
						style={{ maxWidth: "230px", width: "100%" }}
					>
						<CorporateAutoComplete
							filterCorporate={filterCorporate}
							setFilterCorporate={setFilterCorporate}
						/>
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}
						md={6}
						style={{ maxWidth: "230px", width: "100%" }}
					>
						<CampaignAutoComplete
							filterCorporate={filterCorporate}
							filterCampaign={filterCampaign}
							setFilterCampaign={setFilterCampaign}
						/>
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}
						md={6}
						style={{ maxWidth: "230px", width: "100%" }}
					>
						<TextField
							InputLabelProps={{ shrink: true }}
							id="dateRange"
							label={t("date", { ns: "common" })}
							placeholder="dd/mm/yyyy | dd/mm/yyyy"
							value={
								datePicker?.[0]?.startDate
									? `${format(datePicker?.[0]?.startDate, "dd/MM/yyyy") ?? ""} - ${format(datePicker?.[0]?.endDate, "dd/MM/yyyy") ?? ""}`
									: undefined
							}
							inputProps={{ readOnly: true }}
							InputProps={{
								endAdornment: (
									<InputAdornment position="start">
										<CalendarTodayIcon />
									</InputAdornment>
								),
							}}
							sx={{}}
							variant="standard"
							onClick={handleClickToggleDatePicker}
						/>
						<Dialog
							maxWidth="60vw"
							open={openDatePicker}
							onClose={handleClickToggleDatePicker}
						>
							<DialogContent>
								<DateRangePicker
									color="red"
									onChange={(item) => setDatePicker([item.selection])}
									// showSelectionPreview={true}
									dateDisplayFormat={"dd/MM/yyyy"}
									moveRangeOnFirstSelection={false}
									minDate={addDays(new Date(), -(365 * 4))}
									maxDate={new Date()}
									months={2}
									ranges={datePicker}
									direction="horizontal"
									locale={locales[localesSelected][localesSelected]}
									staticRanges={ruStaticRanges}
									inputRanges={[]}
									preventSnapRefocus={true}
									calendarFocus="backwards"
								/>
							</DialogContent>
							<DialogActions>
								<Grid container justifyContent={"space-between"}>
									<Button onClick={handleClickToggleDatePicker}>OK</Button>
								</Grid>
							</DialogActions>
						</Dialog>
					</Grid>
					<Grid item xs={12} sm={12} md={6} style={{ maxWidth: "230px" }}>
						<StatusMultiSelect
							statusSelected={statuses}
							setStatusSelected={setStatuses}
						/>
					</Grid>
				</Grid>
				<Grid
					container
					justifyContent={"space-between"}
					alignItems={"center"}
					sx={{ backgroundColor: "#F1F1F1", padding: "0 16px" }}
				>
					<Grid item sx={{ width: "calc(50% - 32px)", padding: "16px 0" }}>
						<Button
							sx={{ width: "100%", marginLeft: "16px" }}
							variant="outlined"
							onClick={resetFilter}
						>
							{t("filter.buttons.cancel", { ns: "distinte" })}
						</Button>
					</Grid>
					<Grid item sx={{ width: "calc(50% - 32px)", padding: "16px 0" }}>
						<Button
							variant="contained"
							onClick={() => {
								handleSearch()
								handleClose()
							}}
							style={{ width: "100%", marginRight: "16px" }}
						>
							{t("filter.buttons.send", { ns: "distinte" })}
						</Button>
					</Grid>
				</Grid>
			</Popover>
		</div>
	)
}

export default FiltersFile
